import { useEffect, useRef, useState } from 'react';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import { ApiError } from '../../api/client';
import { money, num } from '../../format';
import { useAuth } from '../auth/AuthContext';
import { useNavigation } from '../layout/navigation';
import { getComponent } from '../components/api';
import { StockOperationForm } from './StockOperationForm';
import { requirePosting } from './stockOperation';
import {
  movements as fetchMovements,
  placements as fetchPlacements,
  prices as fetchPrices,
  postMovement,
  reverseMovement,
  transfer,
  type DocumentRef,
  type MovementInput,
  type Posting,
  type TransferInput,
} from './api';

interface Props {
  componentId: string;
  componentName: string;
  /** Shown next to every quantity. A number without its unit is a number that
   *  gets read as pieces when it is metres. */
  unit?: string | null;
}

/**
 * Where a component is, what it cost, and what has happened to it.
 *
 * This is the answer the whole system exists to give, so it leads with the
 * places. The quantity sits immediately before the path rather than at the far
 * end of the row: "47 шт — Шкаф 2 → Полка 3 → Коробка A5" is one fact, and with
 * the two pushed to opposite margins the eye has to pair them up again on every
 * line.
 */
/**
 * Rows of ledger shown at once.
 *
 * Twenty is about a screen, and a screen is the whole point: a part that gets
 * printed with earns a ledger row per print run, and the card has to stay one
 * card long.
 */
const PAGE = 20;

export function StockPanel({ componentId, componentName, unit }: Props) {
  const { t } = useTranslation();
  const { open } = useNavigation();
  const { can } = useAuth();
  const queryClient = useQueryClient();
  const [error, setError] = useState<string | null>(null);
  const [warning, setWarning] = useState<Posting['negative']>([]);
  const [page, setPage] = useState(0);
  const historyHeading = useRef<HTMLHeadingElement>(null);

  // Otherwise the card of the next part opens on the fourth page of the last
  // one's history — and looks empty.
  useEffect(() => setPage(0), [componentId]);

  const canPost = can('stock.post');
  const canReverse = can('stock.reverse');
  const canCost = can('cost.read');

  const places = useQuery({
    queryKey: ['stock', 'placements', componentId],
    queryFn: () => fetchPlacements(componentId),
  });
  const history = useQuery({
    queryKey: ['stock', 'movements', componentId, page],
    // One row more than is shown: its presence is the whole of what the "next"
    // button needs to know, and it costs nothing.
    queryFn: () => fetchMovements(componentId, PAGE + 1, page * PAGE),
    // The previous page stays on screen while the next one is fetched, so
    // paging does not blink through an empty list on every press.
    placeholderData: keepPreviousData,
  });
  const cost = useQuery({
    queryKey: ['stock', 'prices', componentId],
    queryFn: () => fetchPrices(componentId),
    enabled: canCost,
  });

  const refresh = () => {
    void queryClient.invalidateQueries({ queryKey: ['stock'] });
    void queryClient.invalidateQueries({ queryKey: ['components'] });
  };

  const onDone = (result: Posting) => {
    setError(null);
    setWarning(result.negative);
    setPage(0);
    refresh();
  };
  const onFail = (exc: unknown) =>
    setError(exc instanceof ApiError ? exc.code : 'unknown');

  const post = useMutation({
    mutationFn: (input: MovementInput) => postMovement(input).then(requirePosting),
    onSuccess: onDone, retry: false, networkMode: 'always',
  });
  const move = useMutation({
    mutationFn: (input: TransferInput) => transfer(input).then(requirePosting),
    onSuccess: onDone, retry: false, networkMode: 'always',
  });
  const undo = useMutation({
    mutationFn: (id: string) => reverseMovement(id),
    onSuccess: onDone,
    onError: onFail,
  });

  const fetched = history.data ?? [];
  const shown = fetched.slice(0, PAGE);
  const hasNext = fetched.length > PAGE;
  const review = async () => {
    setPage(0);
    await Promise.all([
      places.refetch({ throwOnError: true }),
      queryClient.fetchQuery({
        queryKey: ['stock', 'movements', componentId, 0],
        queryFn: () => fetchMovements(componentId, PAGE + 1, 0),
        staleTime: 0, retry: false,
      }),
      queryClient.fetchQuery({
        queryKey: ['components', componentId], queryFn: () => getComponent(componentId),
        staleTime: 0, retry: false,
      }),
    ]);
    historyHeading.current?.scrollIntoView({ block: 'start' });
    historyHeading.current?.focus({ preventScroll: true });
  };

  return (
    <section className="stock">
      <h3>{t('stock.whereIsIt')}</h3>

      {places.data?.length === 0 && <p className="panel__hint">{t('stock.nowhere')}</p>}
      {/* Quantity first and in a fixed column, then the path with the box it
          actually sits in picked out: the numbers stay scannable down the list,
          and each one is still next to the place it belongs to. */}
      <ul className="stock__places stock__places--where">
        {(places.data ?? []).map((place) => (
          <li key={place.location.id}>
            <span
              className={`stock__amount${Number(place.qty) < 0 ? ' stock__negative' : ''}`}
            >
              <strong>{num(place.qty)}</strong>
              {unit && <em> {unit}</em>}
            </span>
            <span className="stock__path">
              {place.ancestors.length > 0 && <em>{place.ancestors.join(' → ')} → </em>}
              <button type="button" className="link" onClick={() => open('location', place.location.id)}>
                {place.location.name}
              </button>
            </span>
          </li>
        ))}
      </ul>

      {/* What it cost, for those allowed to see it. Two figures, because they
          answer different questions: the last price is what to expect from the
          next order, the average is what the shelf is worth. */}
      {canCost && cost.data && (cost.data.last_unit_cost || cost.data.averages.length > 0) && (
        <>
          <h3>{t('stock.prices')}</h3>
          <dl className="panel__facts stock__prices">
            {cost.data.last_unit_cost && (
              <div className="panel__fact">
                <dt>{t('stock.lastPrice')}</dt>
                <dd>
                  {money(cost.data.last_unit_cost, cost.data.last_currency)}
                  {cost.data.last_at && (
                    <em className="stock__when">
                      {' '}
                      {new Date(cost.data.last_at).toLocaleDateString()}
                    </em>
                  )}
                  {cost.data.last_document && <Document document={cost.data.last_document} />}
                </dd>
              </div>
            )}
            {cost.data.averages.map((row) => (
              <div className="panel__fact" key={row.currency}>
                <dt>{t('stock.averagePrice')}</dt>
                <dd>
                  {money(row.average, row.currency)}
                  <em className="stock__when">
                    {' '}
                    {t('stock.overReceipts', { n: row.receipts, qty: num(row.quantity) })}
                  </em>
                </dd>
              </div>
            ))}
          </dl>
        </>
      )}

      {warning.length > 0 && (
        <p className="stock__warning">
          {t('stock.wentNegative', { n: warning.map((row) => row.qty).join(', ') })}
        </p>
      )}
      {error && (
        <p className="panel__error">{t(`stock.errors.${error}`, t('stock.errors.unknown'))}</p>
      )}

      {canPost && (
        <StockOperationForm
          componentId={componentId}
          componentName={componentName}
          unit={unit}
          places={places.data ?? []}
          onPost={(input) => post.mutateAsync(input)}
          onTransfer={(input) => move.mutateAsync(input)}
          onReview={review}
        />
      )}

      <h3 ref={historyHeading} tabIndex={-1}>{t('stock.history')}</h3>
      {history.isFetching && <p className="panel__hint" role="status">{t('common.loading')}</p>}
      <ul className="stock__history" aria-busy={history.isFetching}>
        {shown.map((row) => (
          <li key={row.id}>
            <span className="stock__when">
              {new Date(row.occurred_at).toLocaleString()}
            </span>
            <span className={`stock__type stock__type--${row.movement_type}`}>
              {t(`stock.type.${row.movement_type}`)}
            </span>
            <strong className={Number(row.quantity) < 0 ? 'stock__negative' : undefined}>
              {Number(row.quantity) > 0 ? '+' : ''}
              {num(row.quantity)}
            </strong>
            {row.location && <MovementLocation location={row.location} />}
            {/* The order it arrived on, the project it went to, the count it
                came out of. Without this the row says a number changed and
                leaves you to find out why somewhere else. */}
            {row.document && <Document document={row.document} />}
            {canCost && row.unit_cost && (
              <em className="stock__cost">{money(row.unit_cost, row.currency)}</em>
            )}
            {row.note && <em className="stock__note">{row.note}</em>}
            {/* Reversal writes a mirror row; it never edits or removes this one,
                which is why the button says "reverse" and not "delete". */}
            {/* Asks first: a reversal is a permanent entry in the ledger that
                moves stock, and the button sits next to every row in the
                history, where the cursor already is. */}
            {canReverse && row.movement_type !== 'reversal' && (
              <button
                type="button"
                onClick={() => {
                  if (window.confirm(t('stock.reverseConfirm', { n: num(row.quantity) }))) {
                    undo.mutate(row.id);
                  }
                }}
              >
                {t('stock.reverse')}
              </button>
            )}
          </li>
        ))}
      </ul>
      {shown.length === 0 && page === 0 && <p className="panel__hint">{t('stock.noHistory')}</p>}

      {/* Pages rather than "show more": the card stays one card long whichever
          page is open, which is the whole reason a hundred-row ledger needed
          doing something about. */}
      {(page > 0 || hasNext) && (
        <nav className="stock__pages">
          <button type="button" disabled={page === 0 || history.isFetching} onClick={() => setPage(page - 1)}>
            {t('stock.prevPage')}
          </button>
          <span className="hint">{!history.isPlaceholderData && t('stock.page', { n: page + 1 })}</span>
          <button type="button" disabled={!hasNext || history.isFetching} onClick={() => setPage(page + 1)}>
            {t('stock.nextPage')}
          </button>
        </nav>
      )}
    </section>
  );
}

/** The order chip answers why a quantity changed; this one answers where.
 *
 * A transfer has two ledger rows by design. The outgoing row carries the
 * source, the incoming one carries the destination, so each is both readable
 * on its own and an ordinary link to the place in question. */
function MovementLocation({ location }: { location: import('../locations/api').LocationBrief }) {
  const { open, canOpen } = useNavigation();

  if (!canOpen('location')) {
    return <em className="stock__doc" title={location.name}>{location.code}</em>;
  }

  return (
    <button
      type="button"
      className="stock__doc stock__doc--link"
      title={location.name}
      onClick={() => open('location', location.id)}
    >
      {location.code}
    </button>
  );
}

/**
 * The document behind a movement, as a chip that opens it.
 *
 * A stocktake has no screen of its own yet, and orders are behind a permission,
 * so the chip falls back to a plain label rather than a link that leads nowhere
 * or answers with 403.
 */
function Document({ document }: { document: DocumentRef }) {
  const { t } = useTranslation();
  const { open, canOpen } = useNavigation();

  const label = `${t(`stock.document.${document.kind}`)} ${document.code}`;
  const reachable =
    (document.kind === 'purchase' || document.kind === 'project') && canOpen(document.kind);

  if (!reachable) {
    return (
      <em className="stock__doc" title={document.title ?? undefined}>
        {label}
      </em>
    );
  }

  return (
    <button
      type="button"
      className="stock__doc stock__doc--link"
      title={document.title ?? undefined}
      onClick={() => open(document.kind as 'purchase' | 'project', document.id)}
    >
      {label}
    </button>
  );
}
